import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@nexus/ui";

import {
  getRevealHref,
  navigateAndReveal,
} from "@/lib/navigation/navigate-and-reveal";

export interface SendSuccessViewProps {
  vendorName: string;
  count: number;
  autoCloseMs: number;
  onDone: () => void;
  orderId?: string;
  onSendAnother?: () => void;
  onViewOrder?: () => void;
  viewOrderHref?: string;
}

export function SendSuccessView({
  vendorName,
  count,
  autoCloseMs,
  onDone,
  orderId,
  onSendAnother,
  onViewOrder,
  viewOrderHref: viewOrderHrefOverride,
}: SendSuccessViewProps) {
  const { t } = useTranslation();
  const doneRef = React.useRef(false);
  const keepOpen = Boolean(orderId || onSendAnother);

  const finish = React.useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone();
  }, [onDone]);

  React.useEffect(() => {
    if (keepOpen) return;
    const timer = window.setTimeout(finish, autoCloseMs);
    return () => window.clearTimeout(timer);
  }, [finish, autoCloseMs, keepOpen]);

  const viewOrderHref =
    viewOrderHrefOverride ??
    (orderId
      ? getRevealHref({
          route: "/marketplace",
          targetId: orderId,
          search: { tab: "in-production", order: orderId },
        })
      : null);

  return (
    <div className="relative flex flex-col items-center px-2 pt-2 pb-1 text-center">
      <div className="relative mb-4 flex h-16 w-16 items-center justify-center">
        <span className="stv-badge flex h-16 w-16 items-center justify-center rounded-full bg-[color-mix(in_oklch,var(--success)_14%,transparent)]">
          <svg
            className="stv-check h-8 w-8"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--success)"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </span>
      </div>

      <DialogHeader className="items-center gap-1.5">
        <DialogTitle
          className="stv-rise text-base font-medium"
          style={{ animationDelay: "120ms" }}
        >
          {t("personaOpportunities.scripts.sendDialog.successTitle")}
        </DialogTitle>
        <DialogDescription
          className="stv-rise text-sm"
          style={{ animationDelay: "180ms" }}
        >
          {t(
            count === 1
              ? "personaOpportunities.scripts.sendDialog.successBody_one"
              : "personaOpportunities.scripts.sendDialog.successBody_other",
            { count, vendorName },
          )}
        </DialogDescription>
      </DialogHeader>

      <div
        className="stv-rise mt-5 flex flex-wrap items-center justify-center gap-2"
        style={{ animationDelay: "240ms" }}
      >
        {viewOrderHref ? (
          <Button
            size="sm"
            asChild
            onClick={(event) => {
              void navigateAndReveal({
                route: "/marketplace",
                targetId: orderId!,
                search: { tab: "in-production", order: orderId },
                event,
                closePanel: finish,
                navigate: () => onViewOrder?.(),
              });
            }}
          >
            <a href={viewOrderHref}>
              {t("marketplace.sendHandoff.viewOrder", "View order")}
            </a>
          </Button>
        ) : null}
        {onSendAnother ? (
          <Button size="sm" variant="outline" onClick={onSendAnother}>
            {t("marketplace.sendHandoff.sendAnother", "Send another")}
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={finish}>
            {t("personaOpportunities.scripts.sendDialog.successDone")}
          </Button>
        )}
      </div>

      {keepOpen ? null : (
        <div className="mt-5 h-0.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="stv-countdown-bar h-full rounded-full bg-success"
            style={
              {
                "--stv-countdown-ms": `${autoCloseMs}ms`,
              } as React.CSSProperties
            }
          />
        </div>
      )}
    </div>
  );
}
